import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { ThemeContext } from '../../context/ThemeContext';
import { checkReviewEligibility, submitReview, updateReview } from '../../services/reviews';
import { getCurrentUser } from '../../services/auth';

const LeaveReviewPage = () => {
  const { darkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);

  const [formData, setFormData] = useState({
    rating: 0,
    title: '',
    content: ''
  });
  const redirectTimeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const user = getCurrentUser();
    if (!user) {
      navigate('/login');
      return;
    }
    fetchEligibility();
  }, [navigate]);

  const fetchEligibility = async () => {
    try {
      setLoading(true);
      const eligibilityData = await checkReviewEligibility();
      setEligibility(eligibilityData);

      // If user has an existing review, load it
      if (eligibilityData.existingReview && eligibilityData.existingReview.submittedAt) {
        setExistingReview(eligibilityData.existingReview);
        setFormData({
          rating: eligibilityData.existingReview.rating || 0,
          title: eligibilityData.existingReview.title || '',
          content: eligibilityData.existingReview.content || ''
        });
      }
    } catch (err) {
      console.error('Error checking eligibility:', err);
      setError('Failed to check review eligibility. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.rating === 0) {
      setError('Please select a rating');
      return;
    }
    if (formData.title.length < 10 || formData.title.length > 100) {
      setError('Title must be between 10 and 100 characters');
      return;
    }
    if (formData.content.length < 20 || formData.content.length > 1000) {
      setError('Review content must be between 20 and 1000 characters');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      let response;
      if (existingReview && eligibility.canEditReview) {
        // Update existing review
        response = await updateReview(formData);
      } else {
        // Submit new review
        response = await submitReview(formData);
      }

      setSuccess(true);
      setExistingReview(response);

      // Show success message for 3 seconds then redirect
      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/account/settings');
      }, 3000);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err.response?.data?.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
    setError(null);
  };

  const renderStars = () => {
    return [...Array(5)].map((_, index) => {
      const starValue = index + 1;
      const isFilled = starValue <= (hoveredStar || formData.rating);

      return (
        <button
          key={index}
          type="button"
          onClick={() => handleRatingClick(starValue)}
          onMouseEnter={() => setHoveredStar(starValue)}
          onMouseLeave={() => setHoveredStar(0)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <Star
            size={40}
            className={isFilled ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        </button>
      );
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING': { text: 'Processing', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'APPROVED': { text: 'Published', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'NEEDS_REVIEW': { text: 'Under Review', color: 'bg-blue-100 text-blue-800', icon: Clock },
      'REJECTED': { text: 'Not Approved', color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };

    const badge = badges[status] || badges['PENDING'];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon size={16} className="mr-1" />
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className={`flex-1 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'} pt-20`}>
        <div className="container mx-auto px-4 py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!eligibility?.isEligible) {
    return (
      <div className={`flex-1 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'} pt-20`}>
        <div className="container mx-auto px-4 py-12 max-w-2xl">
          <div className={`rounded-lg shadow-md p-8 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center mb-4">
              <AlertCircle className="text-yellow-500 mr-3" size={32} />
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Not Eligible Yet
              </h2>
            </div>
            <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {eligibility?.reason || 'You are not currently eligible to leave a review.'}
            </p>
            <button
              onClick={() => navigate('/account/settings')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'} pt-20`}>
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <div className={`rounded-lg shadow-md p-8 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {existingReview ? 'Edit Your Review' : 'Leave a Review'}
          </h1>
            <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Share your experience with Stonewall Invoicing
            </p>

            {/* Existing review status */}
            {existingReview && (
              <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    Status:
                  </span>
                  {getStatusBadge(existingReview.status)}
                </div>
                {eligibility.canEditReview && (
                  <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    You can edit your review until {new Date(existingReview.editableUntil).toLocaleDateString()}
                  </p>
                )}
                {!eligibility.canEditReview && existingReview.submittedAt && (
                  <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Edit window has expired
                  </p>
                )}
              </div>
            )}

            {/* Success message */}
            {success && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="mr-2" size={20} />
                  <span>Review submitted successfully! Redirecting...</span>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="mr-2" size={20} />
                  <span>{error}</span>
                </div>
              </div>
            )}

            {/* Review form */}
            {!success && (eligibility.canSubmitReview || eligibility.canEditReview) && (
              <form onSubmit={handleSubmit}>
                {/* Star rating */}
                <div className="mb-6">
                  <label className={`block mb-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Rating <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    {renderStars()}
                  </div>
                  {formData.rating > 0 && (
                    <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formData.rating} out of 5 stars
                    </p>
                  )}
                </div>

                {/* Title */}
                <div className="mb-6">
                  <label className={`block mb-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Review Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Sum up your experience in a few words"
                    maxLength={100}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {formData.title.length}/100 characters (minimum 10)
                  </p>
                </div>

                {/* Content */}
                <div className="mb-6">
                  <label className={`block mb-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Your Review <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Share your detailed experience with Stonewall Invoicing..."
                    rows={6}
                    maxLength={1000}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {formData.content.length}/1000 characters (minimum 20)
                  </p>
                </div>

                {/* Submit buttons */}
                <div className="flex items-center space-x-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                  >
                    {submitting ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/account/settings')}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                      darkMode
                        ? 'bg-gray-700 text-white hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
        </div>
      </div>
    </div>
  );
};

export default LeaveReviewPage;
